/**
 * Resolves the Control Room API JSON body from a thrown error.
 *
 * Preference order:
 * 1. `error.responseData` — set by `InternalApiClient.errorHandler` after wrapping in ApiResponseError
 * 2. `error.response` when it looks like the JSON body (`message`, `errors`, or `blockers`) — ApiResponseError
 *    stores the body as its `response` field (third constructor arg)
 * 3. `error.response.data` — raw axios error shape, if anything bypasses InternalApiClient
 *
 * @param {Error & { responseData?: object; response?: object }} error
 * @returns {object|null} The API body, or null when none can be found
 */
const resolveApiErrorPayload = error => {
  if (error?.responseData && typeof error.responseData === 'object') {
    return error.responseData;
  }

  // ApiResponseError.response is the body itself ({ message, errors? }), not an axios response.
  const response = error?.response;
  if (
    response &&
    typeof response === 'object' &&
    (typeof response.message === 'string' ||
      Array.isArray(response.errors) ||
      Array.isArray(response.blockers))
  ) {
    return response;
  }

  if (response?.data && typeof response.data === 'object') {
    return response.data;
  }

  return null;
};

/**
 * Maps a Control Room API validation response onto Formik `setErrors` field keys.
 *
 * The Express API returns `{ message, errors?: [{ field, message }] }` on 400 responses that
 * carry ApiValidationErrors. Non-field failures still have a `message` but no `errors[]`.
 *
 * @param {Error & { responseData?: object; response?: object }} error
 * @returns {Record<string, string>|null} Formik field errors, or null when not a field validation response
 */
export const getApiFieldErrors = error => {
  const payload = resolveApiErrorPayload(error);

  if (!payload || !Array.isArray(payload.errors) || payload.errors.length === 0) {
    return null;
  }

  const fieldErrors = payload.errors.reduce((acc, { field, message }) => {
    if (field && message) {
      acc[field] = message;
    }
    return acc;
  }, {});

  // An errors[] full of unusable entries must not look like a validation response — callers use
  // truthiness / Object.values and would otherwise open a blank StatusErrorDialog.
  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
};

/**
 * Reads a user-facing message from an API error.
 *
 * Prefers the JSON body's `message` over the wrapped `[Internal API] …` Error.message so
 * callers can show "project not found" instead of the full request log line. Falls back to
 * `error.message` for network failures or anything that never got a response body.
 *
 * @param {Error & { responseData?: object; response?: object }} error
 * @returns {string}
 */
export const getApiErrorMessage = error => {
  const payload = resolveApiErrorPayload(error);

  if (payload && typeof payload.message === 'string' && payload.message.length > 0) {
    return payload.message;
  }

  if (error?.message && typeof error.message === 'string') {
    return error.message;
  }

  return 'Something went wrong';
};

/**
 * Reads verbatim P&L blocker strings from a 409 contract-upload body.
 *
 * @param {Error & { responseData?: object; response?: object }} error
 * @returns {string[]} Blockers when present, otherwise an empty array.
 */
export const getApiErrorBlockers = error => {
  const payload = resolveApiErrorPayload(error);
  if (!payload || !Array.isArray(payload.blockers)) {
    return [];
  }
  return payload.blockers.filter(entry => typeof entry === 'string' && entry.length > 0);
};

/**
 * Reads the Proposals dashboard URL from a gate 409 body, if Agents Dash sent one.
 *
 * @param {Error & { responseData?: object; response?: object }} error
 * @returns {string|null}
 */
export const getApiErrorUrl = error => {
  const payload = resolveApiErrorPayload(error);
  if (payload && typeof payload.url === 'string' && payload.url.length > 0) {
    return payload.url;
  }
  return null;
};

/**
 * Maps any signed-contract API failure onto the dialog Alert list.
 * Prefers verbatim `blockers`; otherwise uses the API message so 5xx/network
 * errors still render inline instead of a snackbar.
 *
 * @param {Error & { responseData?: object; response?: object }} error
 * @returns {{ blockers: string[], url: string|null }}
 */
export const getContractUploadGateError = error => {
  const blockers = getApiErrorBlockers(error);
  return {
    blockers: blockers.length > 0 ? blockers : [getApiErrorMessage(error)],
    url: getApiErrorUrl(error),
  };
};

/**
 * @param {Error} error
 * @returns {boolean}
 */
export const isApiFieldValidationError = error => Boolean(getApiFieldErrors(error));

/**
 * Duplicate companyName is a 409 whose message includes "existing company with name" (create and
 * rename). Status alone is not enough — other 409s (contract upload blockers, HubSpot-id uniqueness)
 * use the same code.
 *
 * @param {Error & { status?: number; responseData?: object; response?: object }} error
 * @returns {boolean}
 */
export const isDuplicateCompanyNameError = error => {
  const message = getApiErrorMessage(error);
  if (typeof message === 'string' && /existing company with name/i.test(message)) {
    return true;
  }

  // HubSpot-id uniqueness is also a companies 409; do not snackbar it as a name collision.
  if (typeof message === 'string' && /hubspotCompanyId/i.test(message)) {
    return false;
  }

  // Fallback when the body message is missing but the HTTP status is the uniqueness 409.
  // Associated-deal delete 409s also mention "company"; do not treat them as name collisions.
  if (typeof message === 'string' && /associated deals/i.test(message)) {
    return false;
  }

  return error?.status === 409 && /compan/i.test(String(error?.message || ''));
};

/**
 * Duplicate hubspotCompanyId is a 409 whose message includes "existing company with hubspotCompanyId".
 *
 * @param {Error & { status?: number; responseData?: object; response?: object }} error
 * @returns {boolean}
 */
export const isDuplicateHubspotCompanyIdError = error => {
  const message = getApiErrorMessage(error);
  return typeof message === 'string' && /existing company with hubspotCompanyId/i.test(message);
};

/**
 * Delete is blocked while deals still point at the company. API returns 409.
 *
 * @param {Error & { status?: number; responseData?: object; response?: object }} error
 * @returns {boolean}
 */
export const isCompanyHasAssociatedDealsError = error => {
  const message = getApiErrorMessage(error);
  return (
    typeof message === 'string' &&
    /cannot delete a company that has associated deals/i.test(message)
  );
};

/**
 * Voltage Control cannot be renamed (exact `companyName`, not document id). API returns 400.
 *
 * @param {Error & { status?: number; responseData?: object; response?: object }} error
 * @returns {boolean}
 */
export const isVoltageControlRenameError = error => {
  const message = getApiErrorMessage(error);
  return typeof message === 'string' && /cannot rename the voltage control company/i.test(message);
};

/**
 * Duplicate methodology title is a 409 whose message includes "existing project methodology with title"
 * (create and rename).
 *
 * @param {Error & { status?: number; responseData?: object; response?: object }} error
 * @returns {boolean}
 */
export const isDuplicateProjectMethodologyTitleError = error => {
  const message = getApiErrorMessage(error);
  return typeof message === 'string' && /existing project methodology with title/i.test(message);
};

/**
 * Delete is blocked while projects still point at the methodology. API returns 409.
 *
 * Match the API body, the wrapped InternalApiClient message, or a DELETE 409 on this
 * resource when the body message is missing (axios/empty-body 409s otherwise look generic).
 *
 * @param {Error & { status?: number; responseData?: object; response?: object }} error
 * @returns {boolean}
 */
export const isMethodologyHasAssociatedProjectsError = error => {
  const message = getApiErrorMessage(error);
  if (typeof message === 'string' && /used by one or more projects/i.test(message)) {
    return true;
  }

  const status = error?.status ?? error?.response?.status;
  const haystack = `${error?.message || ''} ${message || ''}`;
  return status === 409 && /DELETE/i.test(haystack) && /project-methodolog/i.test(haystack);
};
